import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { lastValueFrom } from 'rxjs';
import { InstanceRights, RestrictData, RestrictService } from '../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { CompanyService } from '../../Entity/Company/company.service';
import { InstanceRepository } from '../../Entity/Instance/instance.repository';
import { NavigationService } from '../../Layout/navigation.service';
import { BreadCrumb } from '../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { MessageModalButtons, MessageModalDisplayMode, MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { UILibModule } from '../../UI-Lib/UILib.module';
import { DescriptorHelperService } from '../descriptor.helper.service';
import { DescriptorOverride, DescriptorOverrideDetail } from '../descriptor.model';
import { DescriptorOverridesService } from '../descriptor.overrides.service';
import { PropertyCharacteristicsPickerComponent } from '../Picker/property.characteristics.picker.component';
import DescriptorUsageEnum = Core.DescriptorUsageEnum;

const warningMessage = 'Editing is in progress. Are you sure you want to leave?';

@Component({
    selector: 'property-characteristics-overrides',
    templateUrl: './property.characteristics.overrides.component.html',
    styleUrls: ['./property.characteristics.overrides.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        PropertyCharacteristicsPickerComponent,
        PopoverDirective,
        UILibModule
    ]
})
export class PropertyCharacteristicsOverridesComponent implements OnInit {
    constructor(private readonly _descriptorOverridesService: DescriptorOverridesService,
                private readonly _helperService: DescriptorHelperService,
                private readonly _upgradeNavigationService: UpgradeNavigationServiceHandler,
                private readonly _navigationService: NavigationService,
                private readonly _instanceRepository: InstanceRepository,
                private readonly _messageModalService: MessageModalService,
                private readonly _restrictService: RestrictService,
                private readonly _companyService: CompanyService) {
    }

    companyId: number;
    pageTitle: string;
    breadcrumbs: BreadCrumb[] = [];
    hasWritePermission: boolean = false;
    hideComplianceFeatures: boolean = false;
    usages: Weissman.Model.Descriptors.DescriptorUsage[];
    descriptorOverrides: DescriptorOverride[];
    descriptorOverrideToEdit: DescriptorOverride;
    isLoading: boolean = true;
    serverActionSingle: object = {};
    isLoadingAssetUsage: object = {};

    private _company: any;
    private _assetUsages: Weissman.Model.Descriptors.DescriptorUsage[];
    private _assetUsagesMinusNever: Weissman.Model.Descriptors.DescriptorUsage[] = [];

    async ngOnInit(): Promise<void> {
        this.companyId = +this._upgradeNavigationService.getQuerystringParam('companyId');

        this._company = await this._companyService.getCompany(this.companyId);

        this.pageTitle = `Property Characteristics Overrides - ${this._company.companyName}`;

        this.breadcrumbs = [{
            name: this._company.companyName,
            target: 'company',
            options: { companyId: this.companyId }
        }];

        await this._doPermissionCheck();
        await this._getData();
    }

    getAssetUsages(canAssetUsageBeRemoved: boolean): Weissman.Model.Descriptors.DescriptorUsage[] {
        return canAssetUsageBeRemoved ? this._assetUsages : this._assetUsagesMinusNever;
    }

    getDescriptorUsage(id: number): string {
        return this.usages.find(x => x.descriptorUsageID === id).name;
    }

    getAssetDescriptorUsage(id: number): string {
        return this._assetUsages.find(x => x.descriptorUsageID === id).name;
    }

    isOverridden(descriptorOverride: DescriptorOverride, usageIdKey: string): boolean {
        return descriptorOverride.descriptorDTO[usageIdKey] !== descriptorOverride.descriptorOverride[usageIdKey];
    }

    isEditMode(descriptorOverride: DescriptorOverride): boolean {
        return this.descriptorOverrideToEdit && descriptorOverride.descriptorOverride.descriptorOverrideID == this.descriptorOverrideToEdit.descriptorOverride.descriptorOverrideID;
    }

    editDescriptorOverride(descriptorOverride: DescriptorOverride): void {
        this.descriptorOverrideToEdit = structuredClone(descriptorOverride);
        this._navigationService.enableNavWarning(warningMessage);
    }

    cancelEdit(): void {
        this.descriptorOverrideToEdit = undefined;
        this._navigationService.disableNavWarning();
    }

    async saveDescriptorOverride(idx: number): Promise<void> {
        this.serverActionSingle[idx] = true;

        await this._descriptorOverridesService.update(this.descriptorOverrideToEdit.descriptorOverride);
        this.descriptorOverrides[idx] = this.descriptorOverrideToEdit;

        this.descriptorOverrideToEdit = undefined;
        this._navigationService.disableNavWarning();

        this.serverActionSingle[idx] = false;
    }

    async deleteDescriptorOverride(descriptorOverride: DescriptorOverride, idx: number): Promise<void> {
        try {
            await this._messageModalService.open({
                message: `Are you sure you want to delete ${descriptorOverride.descriptorName}?`,
                buttons: MessageModalButtons.OKCancel,
                displayMode: MessageModalDisplayMode.SingleMessage
            });

            this.serverActionSingle[idx] = true;

            await this._descriptorOverridesService.deleteById(descriptorOverride.descriptorOverride.descriptorOverrideID);

            this.descriptorOverrides = this.descriptorOverrides.filter(x => x !== descriptorOverride);
            this.serverActionSingle[idx] = false;
        } catch (e) {}
    }

    async descriptorsSelected(descriptors: Core.DescriptorDTO[]): Promise<void> {
        const descriptorsToAdd = descriptors.map(x => {
            const { descriptorID, siteUsageID, parcelUsageID, assetUsageID, companyUsageId } = x as any;
            return {
                descriptorID,
                siteUsageID,
                parcelUsageID,
                assetUsageID,
                companyUsageId,
                companyID: this.companyId
            } as DescriptorOverrideDetail;
        });

        this.isLoading = true;
        try {
            await this._descriptorOverridesService.createBatch(descriptorsToAdd);
            this.descriptorOverrides = await this._getDescriptorOverrides();
        } finally {
            this.isLoading = false;
        }

    }

    private async _doPermissionCheck(): Promise<void> {
        const restrictionData: RestrictData = new RestrictData();
        restrictionData.isCompany = true;
        restrictionData.entityId = this.companyId;
        restrictionData.flag = Core.AccessRightsEnum.Write;

        this.hasWritePermission = await this._restrictService.hasPermission(restrictionData);

        const instanceId = await lastValueFrom(this._instanceRepository.getEntityInstanceId('company', this.companyId));
        this.hideComplianceFeatures = !this._restrictService.hasInstanceRight(InstanceRights.COMPLIANCEFEATURESVIEW, instanceId);
    }

    private async _getData(): Promise<void> {
        this._assetUsagesMinusNever = [];

        const [usages, descriptorOverrides] = await Promise.all([
            this._helperService.getUsages(),
            this._getDescriptorOverrides()
        ]);

        this.usages = usages;
        this.descriptorOverrides = descriptorOverrides;

        this._assetUsages = JSON.parse(JSON.stringify(this.usages));
        this._assetUsages.forEach(assetUsage => {
            if (assetUsage.name == 'Frequent') {
                assetUsage.name = 'Required';
            } else if (assetUsage.name == 'Occasional') {
                assetUsage.name = 'Optional';
            }

            if (assetUsage.name != 'Never') {
                this._assetUsagesMinusNever.push(assetUsage);
            }
        });

        if (!this.hideComplianceFeatures) {
            const assetDescriptorIdsToCheck = this.descriptorOverrides
                .filter(x => x.descriptorOverride.assetUsageID !== DescriptorUsageEnum.Never && x.canAssetUsageBeRemoved)
                .map(x => x.descriptorDTO.descriptorID);

            this.isLoadingAssetUsage = assetDescriptorIdsToCheck.reduce((acc, key) => ({ ...acc, [key]: 1 }), {});

            this._descriptorOverridesService.getAssetUsage(this.companyId, assetDescriptorIdsToCheck).then(descriptorsInfo => {
                descriptorsInfo.forEach(x => {
                    const descriptor = this.descriptorOverrides.find(y => y.descriptorOverride.descriptorID === x.descriptorId);
                    descriptor.canAssetUsageBeRemoved = x.canAssetUsageBeRemoved;
                    delete this.isLoadingAssetUsage[x.descriptorId];
                });
            });
        }

        this.isLoading = false;
    }

    private async _getDescriptorOverrides(): Promise<DescriptorOverride[]> {
        const descriptorOverrides = await this._descriptorOverridesService.getByCompanyId(this.companyId);
        return [...descriptorOverrides].sort((a, b) => {
            const catA = (a.categoryName || '').toString();
            const catB = (b.categoryName || '').toString();
            const categoryCompare = catA.localeCompare(catB);
            if (categoryCompare !== 0) return categoryCompare;

            const seqA = a.descriptorDTO && typeof a.descriptorDTO.sequenceNumber === 'number' ? a.descriptorDTO.sequenceNumber : 0;
            const seqB = b.descriptorDTO && typeof b.descriptorDTO.sequenceNumber === 'number' ? b.descriptorDTO.sequenceNumber : 0;
            return seqA - seqB;
        });
    }
}
